import React, { useContext, useEffect, useState } from 'react';
import theme, { Strong, LinkStyles } from '../../theme';
import MedicalGroupModal from '../Modals/MedicalGroupModal';
import { DataContext } from '../../containers/InsuranceChecker';
import StartOverButton from '../StartOverButton';
import { Grid, Link, useMediaQuery } from '@mui/material';
import { StyledOutOfNetwork, StyledCard, TitleContainer } from './styles';
import CalendarIcon from '../../assets/images/icons/Calendar.svg';
import DoctorIcon from '../../assets/images/icons/Doctor.svg';
import MobileIcon from '../../assets/images/icons/Mobile.svg';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const Icon = ({ src }:{ src:string }) => {
    return (<img src={ src } style={{ height: '50px', marginBottom: '25px' }}/>);
};

type CardProps = {
    icon: string;
    header: string;
    children: JSX.Element;
};
const Card = ({ icon, header, children }:CardProps) => {
    const lessThanSm = useMediaQuery(theme.breakpoints.down('sm'));
    const [ expand, setExpand ] = useState(!lessThanSm);

    const toggleExpand = () => {
        setExpand(!expand);
    };

    const resizeEventHandler = () => {
        const lessThanXs = (window.innerWidth <= theme.breakpoints.values.sm);
        if (!lessThanXs && !expand) {
            setExpand(true);
        }
    };

    useEffect(() => {
        window.addEventListener('resize', resizeEventHandler);
        return () => {
            window.removeEventListener('resize', resizeEventHandler);
        };
    }, []);
    return (
        <Grid item xs={12} sm={4} style={{ padding: '20px 0px 0px 80px' }}>
            <StyledCard>
                <Grid
                    container
                    justifyContent="space-around"
                    alignItems="flex-start"
                    direction="row"
                    className='innerCard'
                >
                    <Grid item xs={2} sm={12}>
                        <Grid
                            container
                            justifyContent={{ xs: 'flex-start', sm: 'space-around' }}
                        >
                            <Icon src={icon}/>
                        </Grid>
                    </Grid>
                    <Grid item xs={9} sm={12}>
                        <Grid
                            container
                            justifyContent={{ xs: 'flex-start', sm: 'space-around' }}
                        >
                            <TitleContainer>
                                <div>
                                    <Strong>{header}</Strong>
                                </div>

                                <div style={{ paddingTop: '10px' }}>
                                    { lessThanSm ?
                                        <FontAwesomeIcon icon={faChevronDown} onClick={ toggleExpand } color='#9E9E9E' className={ expand ? 'rotate down' : 'rotate' } style={{ float: 'right' }}/>
                                        : null
                                    }
                                </div>
                            </TitleContainer>
                            <div className={ lessThanSm && !expand ? 'fadeOutContainer' : '' }>
                                <p style={{ textAlign: lessThanSm ? 'left' : 'center', margin: '0px 0px 20px' }} className={ expand ? 'fadeIn' : 'fadeOut' }>{children}</p>
                            </div>
                        </Grid>
                    </Grid>

                </Grid>
            </StyledCard>
        </Grid>
    );
};

type Props = {
    handleStartOver: () => void;
};

const HMOOutOfNetwork = ({ handleStartOver }:Props): JSX.Element => {
    const { groups } = useContext(DataContext);
    const [ openMedicalGroups, setOpenMedicalGroups ] = useState(false);

    const handleCloseMedicalGroup = () => {
        setOpenMedicalGroups(false);
    };

    return (
        <>
            <StyledOutOfNetwork>
                <Grid
                    container
                    direction="column"
                    justifyContent="center"
                    alignItems="center"
                    style={{ marginBottom: '50px' }}
                >
                    <Grid item xs={12} style={{ width: '100%', background: '#FEEBC8', padding: '0px 20px', borderRadius: '8px 8px 0px 0px' }}>
                        <h2 style={{ fontSize: '1.2rem' }}>You're out of network but becoming in-network with Perlman Clinic is quick, easy, and free!*</h2>
                    </Grid>
                    <Grid item xs={12} style={{ padding: '20px' }}>
                        <strong style={{ textAlign: 'center', textDecoration: 'underline', fontSize: '1.2rem' }}>How to become in-network with Perlman Clinic:</strong>
                    </Grid>
                </Grid>
                <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    spacing={10}
                    style={{ padding: '20px' }}
                >
                    <Card icon={MobileIcon} header={ '1. Call your insurance' }>
                        <span>Call the member services phone number on your insurance card.</span>
                    </Card>
                    <Card icon={DoctorIcon} header={ '2. Switch your Primary Care Provider (PCP)' }>
                        <span>When speaking to them, ask to switch to <Link href='https://perlmanclinic.com/our-team/' style={ LinkStyles } target='_blank' rel='noreferrer'>any primary care physician who works at Perlman Clinic in your county.</Link> For example, San Diego patients can try Dr. Monica Perlman (NPI: 1780796235) or any others in San Diego; Orange County patients can try Dr. Miles Merwin (NPI: 1588702286) or any others in Orange County.</span>
                    </Card>
                    <Card icon={CalendarIcon} header={' 3. Schedule an appointment' }>
                        <span>Once the change is effective, you will have access to ALL Perlman Clinic <Link href='https://perlmanclinic.com/clinic-locations/' style={ LinkStyles } target='_blank' rel='noreferrer'>locations</Link> and <Link href='https://perlmanclinic.com/our-team/' style={ LinkStyles } target='_blank' rel='noreferrer'>providers</Link>!</span>
                    </Card>
                </Grid>
                <Grid
                    container
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                    style={{ padding: '0px 3%' }}
                >
                    <Grid item xs={12}>
                        <p>
                            You can book an appointment after that with any Perlman Clinic provider, at any of our locations. We recommend booking online at <Link href='https://schedule.perlmanclinic.com/' style={{ textDecorationColor: '#000', color: '#000' }} target="new">https://schedule.perlmanclinic.com/</Link>
                        </p>
                        <p>
                            *Note: switch does not apply for plans where Medi-Cal or Medicaid are primary insurance. Also, patients may elect to not use insurance and instead pay our <Link href='https://perlmanclinic.com/pricing/' style={LinkStyles} target="new">cash-pay prices</Link>
                        </p>
                    </Grid>
                </Grid>
            </StyledOutOfNetwork>
            <StartOverButton handleStartOver={handleStartOver}/>
            <MedicalGroupModal open={openMedicalGroups} groups={groups} handleClose={handleCloseMedicalGroup}/>
        </>
    );
};

export default HMOOutOfNetwork;
